import axios from 'axios'

import { extractCollection } from './api.parsers'
import type {
  ApiController,
  ConfigStore,
  CreateOrderRequest,
  CreateOrderResponse,
  GetAvailabilityRequest,
  GetCategoryAvailabilityRequest,
  InvoiceData,
  Product,
  ProductAvailability,
  ProductCategory,
  ProductAvailabilities,
  SendOrderConfirmationRequest
} from './api.types'
import { getCurrentLanguageCode } from './language'

const directApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: {
    Accept: 'application/json',
    'Accept-Language': getCurrentLanguageCode()
  }
})

const directApiToken = process.env.REACT_APP_API_TOKEN
if (directApiToken) {
  directApi.defaults.headers.common.Authorization = `Bearer ${directApiToken}`
}

directApi.interceptors.request.use(config => {
  const languageCode = getCurrentLanguageCode()
  config.headers = config.headers ?? {}
  config.headers['Accept-Language'] = languageCode
  return config
})

const axiosController: ApiController = {
  config: async () => {
    if (!directApi.defaults.baseURL) {
      throw new Error('Missing REACT_APP_API_URL for direct API mode.')
    }

    const { data } = await directApi.get<ConfigStore>('/config')

    return data
  },
  getProductCategories: async () => {
    if (!directApi.defaults.baseURL) {
      throw new Error('Missing REACT_APP_API_URL for direct API mode.')
    }

    const { data } = await directApi.get<unknown>('/product-categories')

    return extractCollection<ProductCategory>(data)
  },
  getProducts: async categoryId => {
    if (!directApi.defaults.baseURL) {
      throw new Error('Missing REACT_APP_API_URL for direct API mode.')
    }

    const encodedCategoryId = encodeURIComponent(String(categoryId))
    const { data } = await directApi.get<unknown>(`/product-categories/${encodedCategoryId}/products`)

    return extractCollection<Product>(data)
  },
  getAvailability: async ({ categoryId, productId, fromDate, toDate }: GetAvailabilityRequest) => {
    if (!directApi.defaults.baseURL) {
      throw new Error('Missing REACT_APP_API_URL for direct API mode.')
    }

    const encodedCategoryId = encodeURIComponent(String(categoryId))
    const encodedProductId = encodeURIComponent(productId)
    const { data } = await directApi.get<ProductAvailability[]>(
      `/product-categories/${encodedCategoryId}/products/${encodedProductId}/availability`,
      { params: { fromDate, toDate } }
    )
    return data
  },
  getCategoryAvailability: async ({ categoryId, fromDate, toDate }: GetCategoryAvailabilityRequest) => {
    if (!directApi.defaults.baseURL) {
      throw new Error('Missing REACT_APP_API_URL for direct API mode.')
    }

    const encodedCategoryId = encodeURIComponent(String(categoryId))
    const { data } = await directApi.get<ProductAvailabilities[]>(
      `/product-categories/${encodedCategoryId}/availability`,
      { params: { fromDate, toDate } }
    )
    return data
  },
  createOrder: async (payload: CreateOrderRequest) => {
    if (!directApi.defaults.baseURL) {
      throw new Error('Missing REACT_APP_API_URL for direct API mode.')
    }

    const { data } = await directApi.post<CreateOrderResponse>('/orders', payload)
    return data
  },
  sendOrderConfirmation: async ({ orderId, email }: SendOrderConfirmationRequest) => {
    if (!directApi.defaults.baseURL) {
      throw new Error('Missing REACT_APP_API_URL for direct API mode.')
    }

    const encodedOrderId = encodeURIComponent(orderId)
    await directApi.post(`/orders/${encodedOrderId}/send-confirmation2`, { email })
  },
  getInvoiceData: async (vatId: string) => {
    if (!directApi.defaults.baseURL) {
      throw new Error('Missing REACT_APP_API_URL for direct API mode.')
    }

    // Endpoint żyje poza poddrzewem sales-device, więc budujemy absolutny URL z roota API.
    const apiRootUrl = directApi.defaults.baseURL.replace(/\/external\/sales-device\/?$/, '')
    const { data } = await directApi.get<InvoiceData>(`${apiRootUrl}/v1/order/invoice-data`, { params: { vatId } })

    return data
  }
}

export default axiosController
